import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Plus, Trash2, Save, Unplug } from "lucide-react";
import { BridgeApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageBody, PageHeader } from "../components/PageScaffold";
import { Button, Pill, EmptyState } from "../components/primitives";
import { cn } from "../lib/cn";
import type { AsanaMentionProject, AsanaMentionConnection } from "../lib/types";

/**
 * Asana @-mentions — the GitHub page's model, for Asana.
 *
 * A comment saying "@sol" on a task in an enabled project spawns sol, which reads
 * the task and replies with a comment. Then it is done: no route is created and
 * nothing is left running.
 *
 * This is NOT the Asana CHANNEL (Channels page). That one watches only tasks the
 * token owner is assignee/follower on and leaves an observer route per task. Both
 * can be on at once — a comment in a project subscribed to both will wake each path.
 */
export default function Asana() {
  const qc = useQueryClient();
  const { data: conn } = useQuery({ queryKey: ["asana", "connection"], queryFn: BridgeApi.asanaMentionConnection });
  const { data: projData, isLoading } = useQuery({ queryKey: ["asana", "projects"], queryFn: BridgeApi.asanaMentionProjects });
  const { data: cfg } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });
  const personaNames = cfg ? Object.keys(cfg.personas) : [];
  const projects = projData?.projects ?? [];
  const connected = !!conn?.connected;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["asana", "projects"] });
    qc.invalidateQueries({ queryKey: ["asana", "connection"] });
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Asana"
        icon={CheckSquare}
        subtitle={connected ? `PAT set · polling every ${conn!.poll_interval_s}s · max ${conn!.max_concurrent} concurrent` : "add an Asana token to let personas answer @-mentions on tasks"}
      />
      <PageBody>
        <ConnectionPanel conn={conn} onSaved={invalidate} />
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-fg">Projects</h2>
            {connected && <AddProject personaNames={personaNames} onSaved={invalidate} />}
          </div>
          {!connected ? (
            <EmptyState icon={<CheckSquare className="size-8" />} title="Not connected" description="Add a personal access token above, then add projects." />
          ) : isLoading ? (
            <p className="text-xs text-fg-subtle">Loading…</p>
          ) : projects.length === 0 ? (
            <EmptyState icon={<CheckSquare className="size-8" />} title="No projects" description="Add a project to choose which personas can be @-mentioned in it." />
          ) : (
            <div className="space-y-3">
              {projects.map((p) => <ProjectEditor key={p.id} project={p} personaNames={personaNames} onSaved={invalidate} />)}
            </div>
          )}
        </div>
      </PageBody>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-fg-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-fg-subtle mt-0.5">{hint}</span>}
    </label>
  );
}

function ConnectionPanel({ conn, onSaved }: { conn?: AsanaMentionConnection; onSaved: () => void }) {
  const toast = useToast();
  const [pat, setPat] = useState("");
  const [interval, setInterval] = useState<number | "">("");
  const [maxConcurrent, setMaxConcurrent] = useState<number | "">("");

  const save = useMutation({
    mutationFn: () => BridgeApi.saveAsanaMentionConnection({
      // Blank = keep the stored token. The PAT is never sent to the browser, so an
      // untouched field must not be read as "clear it".
      personal_access_token: pat || undefined,
      poll_interval_s: interval === "" ? undefined : Number(interval),
      max_concurrent: maxConcurrent === "" ? undefined : Number(maxConcurrent),
    }),
    onSuccess: () => { toast.success("Asana connection saved"); setPat(""); onSaved(); },
    onError: (e) => toast.error("Save failed", String(e)),
  });
  const toggleEnabled = useMutation({
    mutationFn: (enabled: boolean) => BridgeApi.saveAsanaMentionConnection({ enabled }),
    onSuccess: () => { toast.success("Updated"); onSaved(); },
    onError: (e) => toast.error("Toggle failed", String(e)),
  });
  const disconnect = useMutation({
    mutationFn: () => BridgeApi.disconnectAsanaMentions(),
    onSuccess: () => { toast.success("Token forgotten — projects kept"); onSaved(); },
    onError: (e) => toast.error("Disconnect failed", String(e)),
  });

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-fg">Connection (personal access token)</h2>
        {conn?.connected && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-fg-muted" title="Disable to stop polling without losing this config">
              <input
                type="checkbox" className="size-3.5"
                checked={conn.enabled}
                disabled={toggleEnabled.isPending}
                onChange={(e) => toggleEnabled.mutate(e.target.checked)}
              />
              Enabled
            </label>
            <Pill tone={conn.enabled ? "success" : "warning"}>{conn.enabled ? "polling" : "paused"}</Pill>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field
          label={conn?.connected ? "Token (set — leave blank to keep)" : "Token"}
          hint="asana.com → My settings → Apps → Manage developer apps"
        >
          <input className="w-full font-mono text-xs" type="password" value={pat} onChange={(e) => setPat(e.target.value)} placeholder="2/1234…" />
        </Field>
        <Field label="Poll interval (s)" hint="min 30; Asana is polled, not pushed">
          <input className="w-full" type="number" value={interval} onChange={(e) => setInterval(e.target.value === "" ? "" : Number(e.target.value))} placeholder={String(conn?.poll_interval_s ?? 120)} />
        </Field>
        <Field label="Max concurrent" hint="mentions handled at once, fleet-wide">
          <input className="w-full" type="number" value={maxConcurrent} onChange={(e) => setMaxConcurrent(e.target.value === "" ? "" : Number(e.target.value))} placeholder={String(conn?.max_concurrent ?? 4)} />
        </Field>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!conn?.connected && !pat}><Save className="size-3.5" />Save connection</Button>
        {conn?.connected && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => { if (confirm("Forget the Asana token? Projects and persona bindings are kept.")) disconnect.mutate(); }}>
            <Unplug className="size-3.5" />Disconnect
          </Button>
        )}
      </div>
      <p className="text-[11px] text-fg-subtle mt-2">
        The token is stored on the host and never sent back to this page. It must belong to an
        account that can see every project you add below — the fleet posts its replies as that account.
      </p>
    </div>
  );
}

function AddProject({ personaNames, onSaved }: { personaNames: string[]; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  if (!open) return <Button size="sm" variant="secondary" onClick={() => setOpen(true)}><Plus className="size-3.5" />Add project</Button>;
  return <ProjectEditor personaNames={personaNames} onSaved={() => { setOpen(false); onSaved(); }} onCancel={() => setOpen(false)} isNew />;
}

function ProjectEditor({ project, personaNames, onSaved, onCancel, isNew }: {
  project?: AsanaMentionProject; personaNames: string[]; onSaved: () => void; onCancel?: () => void; isNew?: boolean;
}) {
  const toast = useToast();
  const [gid, setGid] = useState(project?.project_gid ?? "");
  const [name, setName] = useState(project?.name ?? "");
  const [enabled, setEnabled] = useState(project ? project.enabled === 1 : true);
  const [instructions, setInstructions] = useState(project?.instructions ?? "");
  const [trusted, setTrusted] = useState(project?.trusted_users ? (JSON.parse(project.trusted_users) as string[]).join(", ") : "");
  // Allowlist: persona -> optional role note. Presence = may be @-mentioned here.
  const [allowed, setAllowed] = useState<Record<string, string>>(
    Object.fromEntries((project?.personas ?? []).map((p) => [p.persona, p.role_note ?? ""])),
  );
  const toggle = (p: string) => setAllowed((a) => { const n = { ...a }; if (p in n) delete n[p]; else n[p] = ""; return n; });
  const setNote = (p: string, note: string) => setAllowed((a) => ({ ...a, [p]: note }));

  const save = useMutation({
    mutationFn: () => BridgeApi.saveAsanaMentionProject({
      project_gid: gid.trim(), name: name || null, enabled, instructions: instructions || null,
      trusted_users: trusted.split(",").map((s) => s.trim()).filter(Boolean),
      personas: Object.entries(allowed).map(([persona, role_note]) => ({ persona, role_note: role_note || null, enabled: true })),
    }),
    onSuccess: () => { toast.success(`${name || gid} saved`); onSaved(); },
    onError: (e) => toast.error("Save failed", String(e)),
  });
  const del = useMutation({
    mutationFn: () => BridgeApi.deleteAsanaMentionProject(project!.id),
    onSuccess: () => { toast.success("Project removed"); onSaved(); },
    onError: (e) => toast.error("Delete failed", String(e)),
  });

  const gidValid = /^\d+$/.test(gid.trim());

  return (
    <div className={cn("panel p-4", isNew && "ring-1 ring-accent/40")}>
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Project gid"
          hint={isNew ? "the number in the project URL: app.asana.com/1/<ws>/project/<gid>" : undefined}
        >
          <input className="w-full font-mono text-xs" value={gid} onChange={(e) => setGid(e.target.value)} disabled={!isNew} placeholder="1202122681121515" />
        </Field>
        <Field label="Name" hint="label for this page only"><input className="w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="AYANA App" /></Field>
      </div>
      {isNew && gid && !gidValid && <p className="text-[11px] text-danger mt-0.5">gid must be digits only — copy it from the project URL</p>}
      <label className="flex items-center gap-2 mt-2 text-xs text-fg-muted">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="size-3.5" /> enabled (watch this project for mentions)
      </label>
      <div className="mt-3">
        <Field label="Project instructions" hint="injected into the persona prompt for mentions in this project">
          <textarea className="w-full font-sans text-sm" rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Guest-facing app bugs. Check the booking-engine logs before answering." />
        </Field>
      </div>
      <div className="mt-3">
        <Field
          label="Trusted commenters"
          hint="comma-separated Asana names (or user gids) who may summon a persona; * = anyone who can comment; empty = nobody"
        >
          <input className="w-full font-mono text-xs" value={trusted} onChange={(e) => setTrusted(e.target.value)} placeholder="Harry Sitorus, Catharina Novita  (or *)" />
        </Field>
      </div>

      {/* Personas allowed to be @-mentioned */}
      <div className="mt-4">
        <span className="text-xs font-medium text-fg-muted">Personas allowed to be @-mentioned</span>
        <p className="text-[11px] text-fg-subtle mb-1.5">A checked persona acts when @-named in a comment on any task in this project, then replies on the task and signs the comment. The optional note is its default lane when the mention gives no explicit instruction.</p>
        <div className="space-y-1.5">
          {personaNames.map((p) => {
            const on = p in allowed;
            return (
              <div key={p} className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 w-36 shrink-0 text-xs">
                  <input type="checkbox" checked={on} onChange={() => toggle(p)} className="size-3.5" /> {p}
                </label>
                {on && <input className="flex-1 h-7 text-xs" value={allowed[p]} onChange={(e) => setNote(p, e.target.value)} placeholder="optional role note — e.g. answer guest-app questions" />}
              </div>
            );
          })}
        </div>
        {Object.keys(allowed).length === 0 && <p className="text-[11px] text-fg-subtle mt-1">None allowed — this project won't respond to any mention.</p>}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!gidValid}><Save className="size-3.5" />Save</Button>
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>}
        {project && !isNew && <Button size="sm" variant="ghost" className="ml-auto" onClick={() => { if (confirm(`Remove ${name || gid}?`)) del.mutate(); }}><Trash2 className="size-3.5" />Remove</Button>}
      </div>
    </div>
  );
}
